import type { InputBaseComponentProps, SxProps, Theme } from "@mui/material";

import { InputAdornment, TextField } from "@mui/material";
import { useCallback, useState } from "react";

import { formatPrice, parsePriceInput } from "./parse-price";

export type PriceInputProps = {
  /** Current value (number). */
  value: number;
  /** Called when value is committed (e.g. on blur) or when valid number is typed. */
  onChange: (value: number) => void;
  /** Optional minimum (inclusive). */
  min?: number;
  /** Optional maximum (inclusive). */
  max?: number;
  /** Step for arrow keys / validation; default 0.01 for decimals. */
  step?: number;
  /** Suffix shown after input (e.g. "€"). */
  suffix?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Placeholder when value is 0 or empty. */
  placeholder?: string;
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  fullWidth?: boolean;
  inputProps?: InputBaseComponentProps;
  InputProps?: { endAdornment?: React.ReactNode; [key: string]: unknown };
  sx?: SxProps<Theme>;
  label?: string;
  /** Input id for a11y. */
  id?: string;
  /** When true, always show formatted value (no raw string while typing). */
  readonly?: boolean;
  /** Called after blur and value commit. Use e.g. to trigger form validation. */
  onBlur?: () => void;
};

/**
 * Headless-style price input: value/onChange are numbers; supports decimal typing.
 * While focused, allows typing "12.5" or "12.50"; on blur commits and clamps to min/max.
 */
function PriceInput({
  value,
  onChange,
  min,
  max,
  step = 0.01,
  suffix = "€",
  disabled = false,
  error = false,
  helperText,
  label,
  placeholder = "0.00",
  variant = "standard",
  size = "small",
  fullWidth = true,
  inputProps,
  InputProps: InputPropsProp,
  sx,
  id,
  readonly = false,
  onBlur: onBlurProp,
}: PriceInputProps) {
  const [focused, setFocused] = useState(false);
  const [inputStr, setInputStr] = useState("");

  const clamped = useCallback(
    (n: number) => {
      let v = n;
      if (min != null && Number.isFinite(min)) v = Math.max(min, v);
      if (max != null && Number.isFinite(max)) v = Math.min(max, v);
      return Math.round(v / step) * step;
    },
    [min, max, step]
  );

  const displayValue =
    focused && !readonly ? (inputStr !== "" ? inputStr : "") : formatPrice(value);

  const handleFocus = useCallback(() => {
    setFocused(true);
    setInputStr(Number.isFinite(value) && value !== 0 ? String(value) : "0");
  }, [value]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    const parsed = parsePriceInput(inputStr);
    const next = parsed !== null ? clamped(parsed) : clamped(0);
    onChange(next);
    setInputStr("");
    onBlurProp?.();
  }, [inputStr, clamped, onChange, onBlurProp]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === "" || raw === "." || /^\d*(?:\.\d*)?$/.test(raw)) {
        setInputStr(raw);
        const parsed = parsePriceInput(raw);
        if (parsed !== null) {
          const next = clamped(parsed);
          onChange(next);
        }
      }
    },
    [clamped, onChange]
  );

  const endAdornment = suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : null;

  return (
    <TextField
      id={id}
      label={label}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      sx={{
        ...sx,
      }}
      slotProps={{
        inputLabel: { shrink: label ? true : false },
        htmlInput: {
          min,
          max,
          step,
          "aria-label": "Prix",
          ...inputProps,
        },
        input: {
          endAdornment: InputPropsProp?.endAdornment ?? endAdornment,
          ...InputPropsProp,
        },
      }}
    />
  );
}

export default PriceInput;
